import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '../stores/org';
import { queryKeys, RESOURCE_LIST_FETCH_STALE_MS } from './query-cache-keys';
import { Assert } from '@cotera/utilities';
import { coco, ResourceType } from '@cotera/api';

const { COCO_TOOL_KEY_PREFIX, isAgentToolKey } = coco;

/**
 * Extract agent_key from tool key (e.g. "coco/agent-xxx" -> "agent-xxx").
 *
 * Gated on {@link isAgentToolKey}, not on the bare `coco/` prefix. Every Coco
 * tool lives under `coco/`, and matching all of them made this claim keys like
 * `coco/dataset-create` and answer with the raw slug — which, being first in
 * the `??` chain at every call site, beat the tool's real display name and put
 * "dataset-create" in the chat header. The same check also keeps
 * `coco/agent-editor` — a builtin that happens to share the agent prefix — from
 * being labelled "Agent".
 */
export function agentKeyFromToolKey(toolKey: string): string | null {
  return isAgentToolKey(toolKey)
    ? toolKey.slice(COCO_TOOL_KEY_PREFIX.length)
    : null;
}

/** Display name for id-based agent keys when no title is available. */
export function formatAgentKeyForDisplay(agentKey: string): string {
  if (agentKey.startsWith('agent-') && agentKey.length > 10) {
    return 'Agent';
  }
  return agentKey;
}

/**
 * Shape returned by useAgentsQuery. This is the single cached payload for the
 * `queryKeys.agents.all` key, so it must be the SUPERSET of every consumer's
 * needs — multiple hooks share this key and React Query dedupes by key, so a
 * narrower second `queryFn` would never run (it reads this cached shape). Add
 * fields here rather than introducing a divergent query on the same key.
 */
export type AgentsListData = {
  agents: Array<{
    id: string;
    agentKey: string;
    title: string | null;
    emoji: string | null;
    model: string;
    description: string | null;
  }>;
};

/** Shared hook that fetches the agents list via resource.list. Backs every
 * `queryKeys.agents.all` consumer (display names, id-by-key, agent options). */
export function useAgentsQuery() {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useQuery({
    queryKey: queryKeys.agents.all({ orgId }),
    staleTime: RESOURCE_LIST_FETCH_STALE_MS,
    queryFn: async (): Promise<AgentsListData> => {
      const res = await client.resource.list({
        type: ResourceType.Agent,
        limit: 100,
      });
      const out = Assert.assertOk(res);
      if (out.type !== 'agent') {
        return { agents: [] };
      }
      return {
        agents: out.items.map((a) => ({
          id: a.id,
          agentKey: a.agentKey,
          title: a.title ?? null,
          emoji: a.emoji ?? null,
          model: a.model,
          description: a.description,
        })),
      };
    },
  });
}

/** Hook that fetches agents list and returns agentKey -> agentId (UUID) map. */
export function useAgentsIdByKey(): Map<string, string> {
  const { data } = useAgentsQuery();
  return useMemo(() => {
    const map = new Map<string, string>();
    if (data?.agents === undefined) {
      return map;
    }
    for (const agent of data.agents) {
      map.set(agent.agentKey, agent.id);
    }
    return map;
  }, [data?.agents]);
}

/** Hook that fetches agents list and returns agentKey -> emoji map (only entries with a non-empty emoji). */
export function useAgentsEmojiByKey(): Map<string, string> {
  const { data } = useAgentsQuery();
  return useMemo(() => {
    const map = new Map<string, string>();
    if (data?.agents === undefined) {
      return map;
    }
    for (const agent of data.agents) {
      if (agent.emoji !== null && agent.emoji?.trim() !== '') {
        map.set(agent.agentKey, agent.emoji);
      }
    }
    return map;
  }, [data?.agents]);
}

/** Hook that fetches agents list and returns agentKey -> displayName map. */
export function useAgentsDisplayNames(): Map<string, string> {
  const { data } = useAgentsQuery();
  return useMemo(() => {
    const map = new Map<string, string>();
    if (data?.agents === undefined) {
      return map;
    }
    for (const agent of data.agents) {
      const displayName =
        (agent.title !== null && agent.title?.trim() !== ''
          ? agent.title?.trim()
          : null) ?? formatAgentKeyForDisplay(agent.agentKey);
      map.set(agent.agentKey, displayName);
    }
    return map;
  }, [data?.agents]);
}

/**
 * Returns a function to resolve display name for an agent tool key.
 * For non-agent keys, returns undefined (caller should use toolKey.displayName()).
 */
export function useAgentDisplayNameResolver(): (
  toolKey: string
) => string | undefined {
  const agentsDisplayNames = useAgentsDisplayNames();
  return useMemo(() => {
    return (toolKey: string): string | undefined => {
      const agentKey = agentKeyFromToolKey(toolKey);
      if (agentKey === null) {
        return undefined;
      }
      return (
        agentsDisplayNames.get(agentKey) ?? formatAgentKeyForDisplay(agentKey)
      );
    };
  }, [agentsDisplayNames]);
}
